import { useCallback, useEffect, useRef, useState } from "react";
import { Panel, PanelGroup } from "rsuite";
import AllFiltersToggle from "./AllFiltersToggle";
import DynamicCheckPicker from "./DynamicCheckPicker";
import DynamicDateRangePicker from "./DynamicDateRangePicker";
import DynamicTextSearch from "./DynamicTextSearch";
import { FilterDefinition, FilterGroup } from "../types";
import { useDataset } from "../DataSet/DatasetProvider";
import DynamicAsyncDropDown from "./DynamicDropDown";

type DisplayStyle = "vertical" | "horizontal";

interface FilterWrapperProps {
  label: string;
  children: React.ReactNode;
}

interface IDynamicFiltersProps {
  showAllFilters?: boolean;
  displayStyle: DisplayStyle;
  onToggleAllFilters?: () => void;
}

const FilterWrapper = ({ label, children }: FilterWrapperProps) => (
  <div className="DynamicFilterItem">
    <div className="DynamicFilterLabel">{label}</div>
    {children}
  </div>
);

interface VerticalFiltersProps {
  filtersDefinition: FilterGroup[];
  resetFilters: () => void;
}

interface HorizontalFiltersProps {
  filters: FilterDefinition[];
  showAllFilters?: boolean;
  onToggleAllFilters?: () => void;
}

const FOCUSABLE_SELECTOR =
  'input, button, select, textarea, [tabindex]:not([tabindex="-1"])';

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const HorizontalFilters = ({
  filters,
  showAllFilters,
  onToggleAllFilters,
}: HorizontalFiltersProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    checkOverflow();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(checkOverflow);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow, showAllFilters]);

  useEffect(() => {
    if (showAllFilters) {
      const timeoutId = setTimeout(() => {
        const firstFocusable = containerRef.current?.querySelector<HTMLElement>(
          FOCUSABLE_SELECTOR,
        );
        firstFocusable?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [showAllFilters]);

  return (
    <>
      <AllFiltersToggle onToggle={onToggleAllFilters} />
      <div
        ref={containerRef}
        className={`inline-filter-container${expanded ? " expanded" : ""}`}
      >
        {filters
          .filter((filter) => filter.pinned || showAllFilters)
          .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
          .map((filter) => (
            <div
              key={filter.code}
              className={`DynamicFilterInline ${filter.code}`}
            >
              {renderFilterByType(filter, "horizontal")}
            </div>
          ))}
      </div>
      {(isOverflowing || expanded) && (
        <button
          className={`expand-filters-btn${expanded ? " expanded" : ""}`}
          onClick={() => setExpanded((prev) => !prev)}
          title={expanded ? "Collapse filters" : "Expand filters"}
          type="button"
        >
          <ChevronDown />
        </button>
      )}
    </>
  );
};

const VerticalFilters = ({
  filtersDefinition,
  resetFilters,
}: VerticalFiltersProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const firstFocusable = containerRef.current?.querySelector<HTMLElement>(
        FOCUSABLE_SELECTOR,
      );
      firstFocusable?.focus();
    }, 0);
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div ref={containerRef}>
      <div className="FilterInfo">
        <p>Filters</p>
        <p onClick={resetFilters} role="button">
          Reset Filters
        </p>
      </div>
      <div className="groups">
        <PanelGroup accordion>
          {filtersDefinition.map((group) => (
            <Panel key={group.name} header={group.name} defaultExpanded>
              {group.filters.map((filter) => (
                <FilterWrapper key={filter.code} label={filter.name}>
                  {renderFilterByType(filter, "vertical")}
                </FilterWrapper>
              ))}
            </Panel>
          ))}
        </PanelGroup>
      </div>
    </div>
  );
};

const renderFilterByType = (
  filter: FilterDefinition,
  displayStyle: DisplayStyle,
) => {
  const commonProps = {
    filterDef: filter,
    placeholder: filter.placeholder || filter.name,
    displayStyle,
  };

  const components: Record<string, React.ReactNode> = {
    search: <DynamicTextSearch {...commonProps} />,

    dateRange: <DynamicDateRangePicker {...commonProps} />,

    multiSelect: <DynamicCheckPicker {...commonProps} />,

    asyncMultiSelect: <DynamicAsyncDropDown {...commonProps} />,
  };

  return components[filter.type] ?? null;
};

const DynamicFilters = ({
  showAllFilters,
  displayStyle,
  onToggleAllFilters,
}: IDynamicFiltersProps) => {
  const { filtersDefinition, resetFilters } = useDataset();

  if (!filtersDefinition?.length) return null;

  const allFilters = filtersDefinition.flatMap(
    (group: FilterGroup) => group.filters,
  );

  return (
    <div className={`DynamicFilter ${displayStyle}`}>
      {displayStyle === "horizontal" && (
        <HorizontalFilters
          filters={allFilters}
          showAllFilters={showAllFilters}
          onToggleAllFilters={onToggleAllFilters}
        />
      )}

      {displayStyle === "vertical" && (
        <VerticalFilters
          filtersDefinition={filtersDefinition}
          resetFilters={resetFilters}
        />
      )}
    </div>
  );
};

export default DynamicFilters;
